import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import Select, { MultiValue } from "react-select";
import { useAuth } from "context/AuthContext";
import { Asset, Suspect, Referral, AssetSuspect } from "types";
import {
  addAsserSuspectsMappingToReferral,
  fetchAssetReferralById,
} from "services/assetRecoveryService";

interface SelectOption {
  value: string | number;
  label: string;
}

interface AddAsserSuspectsMappingPopupProps {
  isOpen: boolean;
  onClose: () => void;
  referralId: string;
  onSuccess?: () => void;
  existingSuspects?: AssetSuspect[];
  existingAssets?: Asset[];
  setReferralData?: React.Dispatch<React.SetStateAction<Referral | null>>;
  referral?: Referral | null;
}

interface ExistingMapping {
  suspectId: string;
  assetId: string;
}

export const AddAsserSuspectsMappingPopup: React.FC<
  AddAsserSuspectsMappingPopupProps
> = ({
  isOpen,
  onClose,
  referralId,
  onSuccess,
  existingSuspects = [],
  existingAssets = [],
  setReferralData,
  referral,
}) => {
  const [formData, setFormData] = useState({
    asset_suspects_ids: [] as string[],
    assets_ids: [] as string[],
  });

  const [loading, setLoading] = useState(false);
  const [existingMappings, setExistingMappings] = useState<ExistingMapping[]>(
    [],
  );

  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [availableAssets, setAssets] = useState<Asset[]>([]);
  const [availableSuspects, setSuspects] = useState<AssetSuspect[]>([]);

  // Build existing mappings from suspect_asset_counts
  useEffect(() => {
    if (referral?.suspect_asset_counts) {
      const mappings: ExistingMapping[] = [];

      referral.suspect_asset_counts.forEach((item) => {
        // For each suspect in this mapping group
        item.asset_suspects.forEach((suspect: any) => {
          // For each asset in this mapping group
          item.assets.forEach((asset: any) => {
            mappings.push({
              suspectId: suspect.id,
              assetId: asset.id,
            });
          });
        });
      });

      setExistingMappings(mappings);
    }
  }, [referral]);

  useEffect(() => {
    // if (referral) {
    //   setAssets(referral.assets || []);
    //   setSuspects(referral.suspects || []);
    // } else {

    fetchAssetReferralById(referralId).then((referralData) => {
      setAssets(referralData.assets || []);
      setSuspects(referralData.suspects || []);
    });
    // }
  }, [isOpen, referral, referralId]);

  // Reset form when popup closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        asset_suspects_ids: [],
        assets_ids: [],
      });
    }
  }, [isOpen]);

  const handleInputChange = (e: {
    target: { name: string; value: string[] };
  }) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      formData.assets_ids.length === 0 &&
      formData.asset_suspects_ids.length === 0
    ) {
      showTooltip(t("please_select_at_least_one_asset_or_suspect"), "error");
      return;
    }

    // Check for duplicate mappings
    const wouldCreateDuplicates: string[] = [];

    formData.asset_suspects_ids.forEach((suspectId) => {
      formData.assets_ids.forEach((assetId) => {
        const isDuplicate = existingMappings.some(
          (mapping) =>
            mapping.suspectId === suspectId && mapping.assetId === assetId,
        );

        if (isDuplicate) {
          const suspect = availableSuspects.find((s) => s.id === suspectId);
          const asset = availableAssets.find((a) => a.id === assetId);
          wouldCreateDuplicates.push(
            `Suspect "${suspect?.person?.full_name || suspectId}" with Asset "${asset?.asset_unique_id || assetId}"`,
          );
        }
      });
    });

    if (wouldCreateDuplicates.length > 0) {
      showTooltip(
        `Cannot create duplicate mappings: ${wouldCreateDuplicates.join(", ")}`,
        "error",
      );
      return;
    }

    try {
      setLoading(true);

      const payload = {
        asset_suspects_ids: formData.asset_suspects_ids,
        assets_ids: formData.assets_ids,
        suspect_count: formData.asset_suspects_ids.length,
        referral: referralId,
      };

      const response = await addAsserSuspectsMappingToReferral(payload);

      showTooltip(t("suspects_added_successfully"), "success");

      if (setReferralData && response) {
        setReferralData(response);
      }

      handleClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error adding suspects:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add suspects";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Filter suspects that haven't been used in ALL possible combinations with selected assets?
  // This is tricky - we need to decide the UX. Option 1: Show all suspects but validate on submit
  const filteredAvailableSuspects = availableSuspects.filter(
    (suspect) =>
      !existingSuspects.some((existing) => existing.id === suspect.id),
  );

  const filteredAvailableAssets = availableAssets.filter(
    (asset) => !existingAssets.some((existing) => existing.id === asset.id),
  );

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Suspects to Investigation"
      description="Select assets and suspects to add to this investigation"
    >
      <form onSubmit={handleSubmit}>
        {/* Assets Selection */}
        <div className="flex flex-col mb-4 mt-2">
          <div className="flex flex-col">
            <label className="referral-form-label mb-2">Select Assets</label>
            <Select<SelectOption, true>
              className="asset-form-field p-5 border border-gray-300 rounded-md"
              classNamePrefix="rs"
              styles={{
                control: (provided) => ({
                  ...provided,
                  border: "none",
                  padding: 0,
                  background: "transparent",
                  margin: "0",
                  color: "black",
                  minHeight: "44px",
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? "#3b82f6"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                  color: state.isSelected ? "white" : "#212121",
                  fontSize: "14px",
                  cursor: "pointer",
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#374151",
                  fontSize: "14px",
                }),
              }}
              isMulti
              options={filteredAvailableAssets.map((asset) => ({
                value: asset.id as string,
                label: asset.asset_unique_id
                  ? asset.asset_unique_id
                  : String(asset.id),
              }))}
              value={
                formData.assets_ids
                  .map((id) => {
                    const asset = availableAssets.find((a) => a.id === id);
                    return asset
                      ? {
                          value: asset.id as string,
                          label: asset.asset_unique_id
                            ? asset.asset_unique_id
                            : String(asset.id),
                        }
                      : null;
                  })
                  .filter(Boolean) as SelectOption[]
              }
              onChange={(selectedOptions: MultiValue<SelectOption>) => {
                const selectedValues = selectedOptions.map(
                  (option) => option.value as string,
                );
                handleInputChange({
                  target: {
                    name: "assets_ids",
                    value: selectedValues,
                  },
                });
              }}
              placeholder="Choose assets to add..."
              noOptionsMessage={() => "No assets available"}
            />
          </div>
        </div>

        {/* Suspects Selection */}
        <div className="flex flex-col mb-4">
          <div className="flex flex-col">
            <label className="referral-form-label mb-2">Select Suspects</label>
            <Select<SelectOption, true>
              className="asset-form-field p-5 border border-gray-300 rounded-md"
              classNamePrefix="rs"
              styles={{
                control: (provided) => ({
                  ...provided,
                  border: "none",
                  padding: 0,
                  background: "transparent",
                  margin: "0",
                  color: "black",
                  minHeight: "44px",
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? "#3b82f6"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                  color: state.isSelected ? "white" : "#212121",
                  fontSize: "14px",
                  cursor: "pointer",
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#374151",
                  fontSize: "14px",
                }),
              }}
              isMulti
              options={filteredAvailableSuspects.map((suspect) => ({
                value: suspect.id as string,
                label: `${suspect.person?.full_name || ""} (${suspect.id})`,
              }))}
              value={
                formData.asset_suspects_ids
                  .map((id) => {
                    const suspect = availableSuspects.find((s) => s.id === id);
                    return suspect
                      ? {
                          value: suspect.id as string,
                          label: `${suspect.person?.full_name || ""} (${suspect.id})`,
                        }
                      : null;
                  })
                  .filter(Boolean) as SelectOption[]
              }
              onChange={(selectedOptions: MultiValue<SelectOption>) => {
                const selectedValues = selectedOptions.map(
                  (option) => option.value as string,
                );
                handleInputChange({
                  target: {
                    name: "asset_suspects_ids",
                    value: selectedValues,
                  },
                });
              }}
              placeholder="Choose suspects to add..."
              noOptionsMessage={() => "No suspects available"}
            />
          </div>
        </div>

        {/* Selected Count Summary */}
        <div className="bg-gray-50 p-3 rounded-md mb-4">
          <p className="text-sm text-gray-600">
            Selected: {formData.assets_ids.length} asset(s),{" "}
            {formData.asset_suspects_ids.length} suspect(s)
          </p>

          {/* Show potential combinations */}
          {formData.assets_ids.length > 0 &&
            formData.asset_suspects_ids.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                This will create{" "}
                {formData.assets_ids.length *
                  formData.asset_suspects_ids.length}{" "}
                mapping(s)
              </p>
            )}
        </div>

        <button
          type="submit"
          disabled={
            loading ||
            (formData.assets_ids.length === 0 &&
              formData.asset_suspects_ids.length === 0)
          }
          className={`mt-4 mb-4 w-full referral-form-button ${
            loading ||
            (formData.assets_ids.length === 0 &&
              formData.asset_suspects_ids.length === 0)
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Adding Suspects..." : "Add Suspects to Investigation"}
        </button>
      </form>
    </BasePopup>
  );
};
